import React, { useEffect, PropsWithChildren } from 'react';
import { Drawer as AntDrawer } from 'antd';
import { clsx } from 'clsx';

import { Colors } from '@common/tokens';
import { LoadingState } from '@common/components/loading-state';
import { hideBodyScroll, showBodyScroll } from '@common/services';

const availableSizes = { sm: 600, lg: 750 };

export type DrawerProps = {
  open: boolean;
  className?: string;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'lg';
  bodyStyle?: React.CSSProperties;
  loading?: boolean;
  onClose?: () => void;
};

/**
 * Prefer wrapping Drawers with a feature component and registering it as a
 * Global Dialog in the _dialogs-map.ts file. Read more in dialogs-readme.md
 */

export function Drawer({
  open,
  className,
  title,
  footer,
  children,
  size,
  bodyStyle,
  loading,
  onClose = () => {},
}: PropsWithChildren<DrawerProps>) {
  const width = availableSizes[size || 'sm'];

  useEffect(() => showBodyScroll, []);

  useEffect(() => {
    if (open) {
      hideBodyScroll();
    } else {
      showBodyScroll();
    }
  }, [open]);

  return (
    <>
      <AntDrawer
        destroyOnClose
        data-testid="wk-drawer"
        title={title}
        footer={footer}
        className={clsx('wk-drawer', className)}
        onClose={onClose}
        open={open}
        width={width}
        bodyStyle={bodyStyle}
      >
        {loading ? <LoadingState /> : children}
      </AntDrawer>
      <style jsx>{`
        :global(.wk-drawer .ant-drawer-title) {
          font-weight: 400;
          font-size: 16px;
          line-height: 28px;
          color: ${Colors.textDefault};
        }
      `}</style>
    </>
  );
}
